import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export const FolderModal = ({ folder, onClose }) => {
  const { t } = useTranslation();
  const [viewer, setViewer] = useState(null); // { file, label }

  useEffect(() => {
    if (!folder) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (viewer) setViewer(null);
        else onClose();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [folder, onClose, viewer]);

  return (
    <AnimatePresence>
      {folder && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 bg-ink/60 backdrop-blur-md"
          onClick={onClose}
          data-testid="folder-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-page border border-ink/15 shadow-[0_40px_120px_-30px_rgba(36,27,23,0.5)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="folder-modal"
          >
            <FolderModalContent folder={folder} onClose={onClose} onOpenViewer={setViewer} t={t} />
          </motion.div>

          <FileViewer viewer={viewer} onClose={() => setViewer(null)} t={t} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const FolderModalContent = ({ folder, onClose, onOpenViewer, t }) => {
  const meta = t(`portfolio.folders.${folder.id}`, { returnObjects: true }) || {};
  const fileLabels = meta.files || {};

  return (
    <div>
      <div
        className="relative px-8 md:px-14 pt-10 pb-8 border-b border-ink/10"
        style={{ borderTopColor: folder.accent, borderTopWidth: '4px' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 md:top-8 md:right-8 w-10 h-10 rounded-full border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-page transition-colors"
          aria-label={t('portfolio.closeFolder')}
          data-testid="folder-modal-close"
        >
          &times;
        </button>
        <div className="max-w-3xl">
          <p className="overline mb-4" style={{ color: folder.stampColor }}>
            {t('portfolio.folderLabel')} · {String(folder.files.length).padStart(2, '0')} {t('portfolio.itemsSuffix')}
          </p>
          <h2 className="display text-4xl md:text-5xl lg:text-6xl text-ink leading-tight mb-3">{meta.name}</h2>
          <p className="text-sm text-muted mb-5">{meta.role}</p>
          <p className="text-base md:text-lg text-ink/80 leading-relaxed">{meta.blurb}</p>
        </div>
      </div>

      <div className="px-8 md:px-14 py-10 md:py-14">
        {folder.files.length === 0 ? (
          <p className="text-muted text-center py-12">{t('portfolio.comingSoon')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {folder.files.map((file, i) => (
              <FileTile
                key={file.key}
                file={file}
                label={fileLabels[file.key] || file.key}
                index={i}
                accent={folder.accent}
                onOpenViewer={onOpenViewer}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FileTile = ({ file, label, index, accent, onOpenViewer }) => {
  const isSite = file.type === 'site';
  const isGallery = file.type === 'gallery';
  const isImage = file.type === 'image';
  const isViewable = isGallery || isImage;

  const inner = (
    <>
      <div
        className="relative overflow-hidden bg-cream aspect-[4/3] mb-3 select-none"
        onContextMenu={(e) => isViewable && e.preventDefault()}
      >
        {isImage && (
          <img
            src={file.src}
            alt={label}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none"
            loading="lazy"
            draggable={false}
          />
        )}
        {isSite && (
          <>
            {file.src ? (
              <img
                src={file.src}
                alt={label}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.06]"
                loading="lazy"
              />
            ) : (
              <div
                className="w-full h-full flex flex-col items-center justify-center gap-3 transition-transform duration-700 group-hover:scale-[1.03]"
                style={{
                  background: `linear-gradient(135deg, #F1E8D0 0%, #EAE0C6 55%, ${accent}30 100%)`,
                }}
              >
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M14 5h5v5M19 5l-8 8" stroke={accent} strokeWidth="1.4" strokeLinecap="round" />
                  <path d="M19 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4" stroke={accent} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <p className="text-[10px] tracking-widest2 uppercase text-muted">
                  {(() => { try { return new URL(file.href).hostname; } catch { return 'Live site'; } })()}
                </p>
              </div>
            )}
            {file.tag && (
              <div
                className="absolute top-3 left-3 px-2.5 py-1 text-[10px] tracking-widest2 uppercase text-page rounded-sm shadow"
                style={{ background: accent }}
              >
                {file.tag}
              </div>
            )}
            <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors duration-500 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100">
              <span className="inline-flex items-center gap-2 text-page bg-ink/80 px-4 py-2 rounded-full text-xs uppercase tracking-widest2 backdrop-blur-sm translate-y-3 group-hover:translate-y-0 transition-transform duration-500">
                Visit live site
                <span aria-hidden>&#8599;</span>
              </span>
            </div>
          </>
        )}
        {isGallery && (
          <>
            <img
              src={file.thumb}
              alt={label}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105 pointer-events-none"
              loading="lazy"
              draggable={false}
            />
            <div
              className="absolute top-2 left-2 px-2 py-1 text-[10px] tracking-widest2 uppercase text-page rounded-sm shadow"
              style={{ background: accent }}
            >
              {file.pages.length} pages
            </div>
            <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/30 transition-colors duration-500 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="inline-flex items-center gap-2 text-page bg-ink/85 px-4 py-2 rounded-full text-xs uppercase tracking-widest2 backdrop-blur-sm">
                View
                <span aria-hidden>&#8599;</span>
              </span>
            </div>
          </>
        )}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-ink font-medium">{label}</p>
        <span className="text-xs text-muted uppercase tracking-widest">{String(index + 1).padStart(2, '0')}</span>
      </div>
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
      className="group"
      data-testid={`folder-file-${file.key}`}
    >
      {isSite ? (
        <a href={file.href} target="_blank" rel="noreferrer" className="block">
          {inner}
        </a>
      ) : isViewable ? (
        <button
          type="button"
          onClick={() => onOpenViewer({ file, label })}
          className="block w-full text-left focus-visible:outline-none"
          data-testid={`folder-file-${file.key}-open`}
        >
          {inner}
        </button>
      ) : (
        inner
      )}
    </motion.div>
  );
};

const FileViewer = ({ viewer, onClose, t }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [viewer]);

  useEffect(() => {
    if (!viewer) return;
    const onKey = (e) => {
      if (viewer.file.type !== 'gallery') return;
      if (e.key === 'ArrowRight') setPage((p) => Math.min(viewer.file.pages.length - 1, p + 1));
      if (e.key === 'ArrowLeft') setPage((p) => Math.max(0, p - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer]);

  return (
    <AnimatePresence>
      {viewer && (
        <motion.div
          key="viewer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-10 bg-ink/85 backdrop-blur-md"
          onClick={onClose}
          onContextMenu={(e) => e.preventDefault()}
          data-testid="file-viewer"
        >
          <div
            className="relative w-full max-w-4xl max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute -top-2 -right-2 md:top-2 md:right-2 z-10 w-10 h-10 rounded-full bg-page text-ink flex items-center justify-center shadow-lg hover:bg-clay hover:text-page transition-colors"
              aria-label={t('portfolio.closeFolder')}
              data-testid="file-viewer-close"
            >
              &times;
            </button>

            {viewer.file.type === 'image' && (
              <img
                src={viewer.file.src}
                alt={viewer.label}
                className="max-w-full max-h-[85vh] object-contain select-none pointer-events-none"
                draggable={false}
              />
            )}

            {viewer.file.type === 'gallery' && (
              <div className="w-full flex flex-col items-center">
                <div className="relative w-full max-h-[80vh] flex items-center justify-center">
                  <img
                    key={viewer.file.pages[page]}
                    src={viewer.file.pages[page]}
                    alt={`${viewer.label} — page ${page + 1}`}
                    className="max-w-full max-h-[80vh] object-contain select-none pointer-events-none"
                    draggable={false}
                    data-testid="file-viewer-page"
                  />
                </div>
                {viewer.file.pages.length > 1 && (
                  <div className="mt-4 flex items-center gap-4 text-page">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="w-10 h-10 rounded-full border border-page/40 flex items-center justify-center hover:bg-page hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-page transition-colors"
                      aria-label="Previous page"
                      data-testid="file-viewer-prev"
                    >
                      &larr;
                    </button>
                    <span className="text-sm tracking-widest2 uppercase min-w-[80px] text-center">
                      {String(page + 1).padStart(2, '0')} / {String(viewer.file.pages.length).padStart(2, '0')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(viewer.file.pages.length - 1, p + 1))}
                      disabled={page === viewer.file.pages.length - 1}
                      className="w-10 h-10 rounded-full border border-page/40 flex items-center justify-center hover:bg-page hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-page transition-colors"
                      aria-label="Next page"
                      data-testid="file-viewer-next"
                    >
                      &rarr;
                    </button>
                  </div>
                )}
              </div>
            )}

            <p className="mt-4 text-page/80 text-sm text-center">{viewer.label}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
